/**
 * Scanditure Custom Wishlist
 * localStorage-based wishlist with Web Components
 */

/* ── Global Wishlist Manager ── */
window.ScanditureWishlist = {
  STORAGE_KEY: 'scanditure_wishlist',

  getItems() {
    try {
      return JSON.parse(localStorage.getItem(this.STORAGE_KEY)) || [];
    } catch {
      return [];
    }
  },

  _save(items) {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(items));
    document.dispatchEvent(
      new CustomEvent('wishlist:change', {
        detail: { count: items.length, items },
      })
    );
  },

  addItem(product) {
    const items = this.getItems();
    if (items.some((i) => i.id === product.id)) return;
    items.push({ ...product, addedAt: Date.now() });
    this._save(items);
  },

  removeItem(id) {
    const items = this.getItems().filter((i) => i.id !== id);
    this._save(items);
  },

  toggleItem(product) {
    if (this.hasItem(product.id)) {
      this.removeItem(product.id);
      return false;
    }
    this.addItem(product);
    return true;
  },

  hasItem(id) {
    return this.getItems().some((i) => i.id === id);
  },

  getCount() {
    return this.getItems().length;
  },

  clear() {
    this._save([]);
  },

  /* Fetch variant ID for items that were saved before we stored it */
  async _resolveVariantId(item) {
    if (item.variantId) return item.variantId;
    try {
      const res = await fetch(`/products/${item.handle}.json`);
      const data = await res.json();
      const variant = data.product.variants[0];
      if (variant) {
        /* Backfill the variant ID into storage */
        const items = this.getItems();
        const found = items.find((i) => i.id === item.id);
        if (found) {
          found.variantId = variant.id;
          this._save(items);
        }
        return variant.id;
      }
    } catch { /* fall through */ }
    return null;
  },

  /* Add all wishlist items to the Shopify cart */
  async addAllToCart() {
    const items = this.getItems();
    if (items.length === 0) return { success: false, error: 'empty' };

    /* Resolve variant IDs for all items */
    const variantIds = await Promise.all(
      items.map((item) => this._resolveVariantId(item))
    );

    const cartItems = variantIds
      .filter((id) => id !== null)
      .map((id) => ({ id, quantity: 1 }));

    if (cartItems.length === 0) return { success: false, error: 'no_variants' };

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: cartItems }),
      });

      if (!res.ok) throw new Error(`Cart API error: ${res.status}`);

      return { success: true, count: cartItems.length };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },
};

/* ── Shared: update Shopify cart bubble in header ── */
function refreshCartBubble() {
  fetch('/cart.js')
    .then((r) => r.json())
    .then((cart) => {
      const icon = document.getElementById('cart-icon-bubble');
      if (!icon) return;

      let bubble = icon.querySelector('.cart-count-bubble');

      if (cart.item_count > 0) {
        /* Create bubble if it doesn't exist (cart was empty on page load) */
        if (!bubble) {
          bubble = document.createElement('div');
          bubble.className = 'cart-count-bubble';
          bubble.innerHTML = '<span aria-hidden="true"></span>';
          icon.appendChild(bubble);
        }
        bubble.style.display = '';
        const span = bubble.querySelector('span[aria-hidden]');
        if (span) span.textContent = cart.item_count;

        /* Swap cart icon from empty to filled */
        const svgWrap = icon.querySelector('.svg-wrapper');
        if (svgWrap && svgWrap.innerHTML.includes('icon-cart-empty')) {
          fetch('/cart', { headers: { Accept: 'text/html' } })
            .catch(() => {}); /* Preload, icon swap happens on next page load */
        }
      }
    })
    .catch(() => {});
}

/* ── <wishlist-button> Web Component ── */
class WishlistButton extends HTMLElement {
  connectedCallback() {
    this.productData = {
      id: Number(this.dataset.productId),
      variantId: Number(this.dataset.productVariantId) || null,
      handle: this.dataset.productHandle,
      title: this.dataset.productTitle,
      image: this.dataset.productImage,
      price: Number(this.dataset.productPrice),
      priceFormatted: this.dataset.productPriceFormatted,
      url: this.dataset.productUrl,
      vendor: this.dataset.productVendor,
    };

    this._sync();
    this.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this._onClick();
    });
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();
        this._onClick();
      }
    });
    document.addEventListener('wishlist:change', () => this._sync());
  }

  _sync() {
    const active = ScanditureWishlist.hasItem(this.productData.id);
    this.classList.toggle('is-wishlisted', active);

    const label = this.querySelector('.wishlist-heart__label');
    if (label) {
      label.textContent = active ? 'Saved to Wishlist' : 'Save to Wishlist';
    }
  }

  _onClick() {
    const added = ScanditureWishlist.toggleItem(this.productData);
    this.classList.toggle('is-wishlisted', added);

    /* Pop animation */
    this.classList.add('wishlist-heart--animating');
    setTimeout(() => this.classList.remove('wishlist-heart--animating'), 350);
  }
}

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}

/* ── Header Count Bubble + Heart Colour ── */
function updateHeaderWishlist() {
  const bubble = document.getElementById('wishlist-count-bubble');
  const heartLink = document.querySelector('.header__icon--wishlist');
  const count = ScanditureWishlist.getCount();

  /* Update count bubble */
  if (bubble) {
    const span = bubble.querySelector('span[aria-hidden]');
    if (count > 0) {
      bubble.style.display = '';
      if (span) span.textContent = count;
    } else {
      bubble.style.display = 'none';
    }
  }

  /* Toggle red heart state on header icon */
  if (heartLink) {
    heartLink.classList.toggle('wishlist-has-items', count > 0);
  }
}

document.addEventListener('DOMContentLoaded', updateHeaderWishlist);
document.addEventListener('wishlist:change', updateHeaderWishlist);

/* ── <wishlist-page> Web Component ── */
class WishlistPage extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('#wishlist-grid');
    this.empty = this.querySelector('#wishlist-empty');
    this.countEl = this.querySelector('.wishlist-page__count');
    this.addAllBtn = this.querySelector('#wishlist-add-all');

    this._render();
    document.addEventListener('wishlist:change', () => this._render());

    this.addEventListener('click', (e) => {
      /* Remove item */
      const removeBtn = e.target.closest('.wishlist-page__item-remove');
      if (removeBtn) {
        e.preventDefault();
        const id = Number(removeBtn.dataset.productId);
        const card = removeBtn.closest('.wishlist-page__item');
        if (card) {
          card.classList.add('wishlist-page__item--removing');
          setTimeout(() => ScanditureWishlist.removeItem(id), 280);
        } else {
          ScanditureWishlist.removeItem(id);
        }
        return;
      }

      /* Single add to cart */
      const atcBtn = e.target.closest('.wishlist-page__item-atc');
      if (atcBtn) {
        e.preventDefault();
        e.stopPropagation();
        this._addSingleToCart(atcBtn);
        return;
      }

      /* Add all to cart */
      const addAllTrigger = e.target.closest('#wishlist-add-all');
      if (addAllTrigger) {
        e.preventDefault();
        this._addAllToCart();
      }
    });
  }

  async _addSingleToCart(btn) {
    if (btn.classList.contains('is-loading') || btn.classList.contains('is-added')) return;

    const handle = btn.dataset.productHandle;
    const productId = Number(btn.dataset.productId);
    const originalText = btn.textContent;

    btn.classList.add('is-loading');
    btn.textContent = 'Adding...';

    /* Resolve variant ID */
    const item = ScanditureWishlist.getItems().find((i) => i.id === productId);
    const variantId = item
      ? await ScanditureWishlist._resolveVariantId(item)
      : null;

    if (!variantId) {
      btn.classList.remove('is-loading');
      btn.textContent = 'Unavailable';
      setTimeout(() => { btn.textContent = originalText; }, 1500);
      return;
    }

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      });

      if (!res.ok) throw new Error('Cart error');

      btn.classList.remove('is-loading');
      btn.classList.add('is-added');
      btn.textContent = 'Added to Cart ✓';
      refreshCartBubble();

      setTimeout(() => {
        btn.classList.remove('is-added');
        btn.textContent = originalText;
      }, 2000);
    } catch {
      btn.classList.remove('is-loading');
      btn.textContent = 'Error';
      setTimeout(() => { btn.textContent = originalText; }, 1500);
    }
  }

  async _addAllToCart() {
    const btn = this.addAllBtn;
    if (!btn || btn.classList.contains('is-loading')) return;

    const label = btn.querySelector('.wishlist-page__add-all-label');
    const originalText = label ? label.textContent : '';

    btn.classList.add('is-loading');
    if (label) label.textContent = 'Adding...';

    const result = await ScanditureWishlist.addAllToCart();

    if (result.success) {
      btn.classList.remove('is-loading');
      btn.classList.add('is-success');
      if (label) label.textContent = `Added ${result.count} ${result.count === 1 ? 'item' : 'items'} to cart`;
      refreshCartBubble();

      setTimeout(() => {
        btn.classList.remove('is-success');
        if (label) label.textContent = originalText;
      }, 2500);
    } else {
      btn.classList.remove('is-loading');
      if (label) label.textContent = 'Something went wrong';
      setTimeout(() => {
        if (label) label.textContent = originalText;
      }, 2000);
    }
  }

  _render() {
    const items = ScanditureWishlist.getItems();

    if (this.countEl) {
      this.countEl.textContent =
        items.length === 0
          ? ''
          : items.length === 1
            ? '1 saved piece'
            : `${items.length} saved pieces`;
    }

    /* Toggle add-all button visibility */
    if (this.addAllBtn) {
      this.addAllBtn.style.display = items.length > 0 ? '' : 'none';
    }

    if (items.length === 0) {
      if (this.grid) this.grid.style.display = 'none';
      if (this.empty) this.empty.style.display = '';
      return;
    }

    if (this.grid) this.grid.style.display = '';
    if (this.empty) this.empty.style.display = 'none';

    if (!this.grid) return;

    this.grid.innerHTML = items
      .sort((a, b) => b.addedAt - a.addedAt)
      .map(
        (item, index) => `
      <article class="wishlist-page__item" style="animation-delay: ${index * 0.06}s">
        <div class="wishlist-page__item-media-wrap" style="position:relative;">
          <a href="${item.url}" class="wishlist-page__item-media">
            <img src="${item.image}" alt="${this._esc(item.title)}" loading="lazy" width="720" height="720">
          </a>
          <button class="wishlist-page__item-atc" data-product-id="${item.id}" data-product-handle="${item.handle}">Add to Cart</button>
        </div>
        <div class="wishlist-page__item-info">
          <p class="wishlist-page__item-vendor">${this._esc(item.vendor)}</p>
          <h3 class="wishlist-page__item-title"><a href="${item.url}">${this._esc(item.title)}</a></h3>
          <p class="wishlist-page__item-price">${item.priceFormatted}</p>
        </div>
        <button class="wishlist-page__item-remove" data-product-id="${item.id}" aria-label="Remove ${this._esc(item.title)} from wishlist">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </article>
    `
      )
      .join('');
  }

  _esc(str) {
    const el = document.createElement('span');
    el.textContent = str || '';
    return el.innerHTML;
  }
}

if (!customElements.get('wishlist-page')) {
  customElements.define('wishlist-page', WishlistPage);
}
